import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import FtModal from './FtModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Fts {
  ft_id: number;
  ft_name: string;
  ft_status: number;
  }

const FabricTypes: React.FC = () => {
  const [fabrictypedata, setFabricTypeData] = useState<Fts[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Fts | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchFt = async () => {
    try {
      const data = await apiRequest<Fts[]>({
        method: 'get',
        url: 'fabric-types',
      });
      setFabricTypeData(data);
    } catch (error) {
      showError('Failed to fetch fabric type');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFt();
  }, []);

  const handleEditClick = (record: Fts) => {
    setIsEditMode(true);
    setInitialValues({
      ft_id: record.ft_id,
      ft_name: record.ft_name,
      ft_status: record.ft_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchFt();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (ft_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-fabric-type/${ft_id}`,
      });
      showSuccess('Fabric Type deleted');
      fetchFt();
    } catch {
      showError('Failed to delete Fabric type');
    }
  };

  const handleStatusToggle = async (ftId: number) => {
    setFabricTypeData((prev) =>
      prev.map((item) =>
        item.ft_id === ftId
          ? { ...item, ft_status: item.ft_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-fabric-type/${ftId}`,
      });
      showSuccess('Fabric Type status updated');
    } catch (error) {
      showError('Failed to update status');
      setFabricTypeData((prev) =>
        prev.map((item) =>
          item.ft_id === ftId
            ? { ...item, ft_status: item.ft_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Fts>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Fts> = [
    {
      title: 'Fabric Type',
      dataIndex: 'ft_name',
      key: 'ft_name',
      filters: Array.from(new Set(fabrictypedata.map((s) => s.ft_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.ft_name || null,
      onFilter: (value, record) => record.ft_name.includes(value as string),
      sorter: (a, b) => a.ft_name.localeCompare(b.ft_name),
      sortOrder: sortedInfo.columnKey === 'ft_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.ft_name}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Fts) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.ft_status}
                onChange={() => handleStatusToggle(record.ft_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this ft?"
              onConfirm={() => handleDelete(record.ft_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Fabric Type</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Fabric Type
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Fts>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="ft_id"
          dataSource={fabrictypedata.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No Fabric Type found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, fabrictypedata.length)} of{' '}
                {fabrictypedata.length}
              </span>
              <Pagination
                {...pagination}
                total={fabrictypedata.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <FtModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default FabricTypes;